/** Website-owner plan: subscribe screen, plan line in the sidebar, and the admin's plans table. */
import { useEffect, useState } from "preact/hooks";

export type BillingInfo = {
  enforcement: boolean;
  required: boolean;
  active: boolean;
  status: string;
  periodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  pricePkr: number;
  credits: number;
  creditsUsed: number;
  creditsSince: number;
  cardPayments: boolean;
  canManage: boolean;
};

async function post(path: string, body?: unknown): Promise<any> {
  const r = await fetch(`/api${path}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body ?? {}) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}
const day = (ts: number | null) => (ts ? new Date(ts).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "–");
const pkr = (n: number) => `PKR ${Math.round(n).toLocaleString()}`;

/** Desktop: Stripe opens in the browser (the window-open handler sends it there); web: same tab. */
function openStripe(url: string, desktop: boolean) {
  if (desktop) window.open(url, "_blank");
  else location.assign(url);
}

/** Shown instead of chat when plan enforcement is on and the owner has no running period. */
export function SubscribeScreen({ billing, desktop, refresh }: { billing: BillingInfo; desktop: boolean; refresh: () => void }) {
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [err, setErr] = useState("");
  // after the browser checkout, check every 5 s for 10 minutes so the app unlocks by itself
  useEffect(() => {
    if (!waiting) return;
    let n = 0;
    const t = setInterval(async () => {
      n++;
      const b = await fetch("/api/billing").then((r) => r.json()).catch(() => null);
      if (b?.active || n > 120) {
        clearInterval(t);
        setWaiting(false);
        refresh();
      }
    }, 5000);
    return () => clearInterval(t);
  }, [waiting]);
  const subscribe = async () => {
    setBusy(true);
    setErr("");
    try {
      const r = await post("/billing/checkout");
      openStripe(r.url, desktop);
      if (desktop) setWaiting(true);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const lapsed = billing.status !== "none" && billing.status !== "incomplete";
  return (
    <div class="empty" style="max-width:520px">
      <h2>{lapsed ? "Your plan has ended" : "Start your Xelent Agent plan"}</h2>
      <p>The agent edits your website, writes blog posts, makes images and schedules social posts for you. One plan, paid monthly by card.</p>
      <div class="card" style="width:auto;text-align:left;margin:22px 0">
        <div style="font-size:28px;font-weight:700;color:var(--fg)">{pkr(billing.pricePkr)} <span style="font-size:15px;font-weight:400;color:var(--muted)">/ month</span></div>
        <ul style="margin:12px 0 18px;padding-left:18px;color:var(--muted)">
          <li>{Math.round(billing.credits).toLocaleString()} credits every month (1 credit = PKR 1)</li>
          <li>Credits renew with each payment</li>
          <li>Cancel or change your card any time</li>
        </ul>
        {billing.cardPayments ? (
          <button class="btn primary" style="width:100%;padding:12px" onClick={subscribe} disabled={busy}>{busy ? "Opening secure checkout…" : `Subscribe — ${pkr(billing.pricePkr)} / month`}</button>
        ) : (
          <div class="hint">Card payments are being set up. Please contact Xelent to activate your plan.</div>
        )}
        {waiting && <p class="hint" style="margin:12px 0 0">Finish the payment in your browser. This screen unlocks by itself when it is done.</p>}
        {err && <div class="error">{err}</div>}
      </div>
      <p class="hint">Payments are handled by Stripe; Xelent never sees your card number.{" "}{billing.canManage && <a href="#" onClick={(e) => { e.preventDefault(); post("/billing/portal").then((r) => openStripe(r.url, desktop)).catch((x) => setErr(x.message)); }}>Manage billing</a>}{" "}<a href="#" onClick={(e) => { e.preventDefault(); refresh(); }}>Refresh</a></p>
    </div>
  );
}

/** Sidebar line for website owners: "Plan active until … · Manage billing". */
export function PlanLine({ billing, desktop }: { billing?: BillingInfo; desktop: boolean }) {
  const [err, setErr] = useState("");
  if (!billing) return null;
  const manage = (e: Event) => {
    e.preventDefault();
    post("/billing/portal").then((r) => openStripe(r.url, desktop)).catch((x) => setErr(x.message));
  };
  if (billing.active)
    return (
      <div style="margin-top:4px">
        {billing.cancelAtPeriodEnd ? "Plan ends" : "Plan active until"} {day(billing.periodEnd)}
        {billing.canManage && <> · <a href="#" onClick={manage}>Manage billing</a></>}
        {err && <div class="error">{err}</div>}
      </div>
    );
  if (!billing.enforcement) return null;
  return <div style="margin-top:4px;color:var(--warn)">No active plan</div>;
}

/** Admin → Usage: each website owner's plan, credits, and grants. */
export function TenantPlansAdmin() {
  const [d, setD] = useState<any>(null);
  const [msg, setMsg] = useState("");
  const load = () => fetch("/api/admin/plans").then((r) => r.json()).then(setD).catch((e) => setMsg(e.message));
  useEffect(() => {
    load();
  }, []);
  if (!d) return <div class="hint">Loading plans…</div>;
  const grant = async (id: string, body: Record<string, unknown>, label: string) => {
    try {
      await post(`/admin/plans/${id}/grant`, body);
      setMsg(label);
      load();
    } catch (e) {
      setMsg((e as Error).message);
    }
  };
  return (
    <div style="margin-bottom:24px">
      <h3>Website owner plans</h3>
      <p class="hint">
        Plan enforcement is <b>{d.enforcement ? "on" : "off"}</b> (Admin → Settings). Card payments {d.cardPayments ? "ready" : "not set up (no Stripe secret key)"}; webhook {d.webhook ? "connected" : "not connected"}. Stripe webhook URL: <code>{d.webhookUrl}</code>{" "}
        {d.cardPayments && <button class="btn small" onClick={() => post("/admin/billing/webhook").then((r) => { setMsg(`Webhook connected (${r.livemode ? "live" : "test"} mode)`); load(); }).catch((e) => setMsg(e.message))}>{d.webhook ? "Reconnect webhook" : "Connect webhook"}</button>}
      </p>
      {d.tenants.length === 0 ? (
        <div class="hint">No website owners have signed in yet.</div>
      ) : (
        <table class="grid">
          <thead><tr><th>Owner</th><th>Status</th><th>Period ends</th><th>Credits used</th><th>Left</th><th>Grant</th></tr></thead>
          <tbody>
            {d.tenants.map((t: any) => (
              <tr>
                <td>{t.name}<div class="hint">{t.email}{t.accountActive ? "" : " · disabled"}</div></td>
                <td>{t.active ? (t.status === "free" ? "free (granted)" : t.status) : t.status === "none" ? "no plan" : `${t.status} (ended)`}{t.cancelAtPeriodEnd ? " · cancels" : ""}{t.livemode === 0 ? " · test" : ""}</td>
                <td>{day(t.periodEnd)}</td>
                <td>{Math.round(t.creditsUsed).toLocaleString()} of {Math.round(t.credits).toLocaleString()}<div class="hint">since {day(t.creditsSince)}</div></td>
                <td>{Math.max(0, Math.round(t.credits - t.creditsUsed)).toLocaleString()}</td>
                <td style="white-space:nowrap">
                  <button class="btn small" onClick={() => confirm(`Give ${t.name} a free month?`) && grant(t.id, { months: 1 }, `Free month given to ${t.name}`)}>+1 free month</button>{" "}
                  <button class="btn small" onClick={() => { const n = Number(prompt("Extra credits for the current period", "500")); if (n > 0) grant(t.id, { credits: n }, `${n} credits added for ${t.name}`); }}>+ credits</button>
                  {t.active && t.status === "free" && <> <button class="btn small danger" onClick={() => confirm(`End ${t.name}'s free period now?`) && grant(t.id, { end_period: true }, "Free period ended")}>End</button></>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {msg && <div class="hint" style="margin-top:8px">{msg}</div>}
    </div>
  );
}
